import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getParticipantSessions,
  deleteAllParticipantSessions,
  updateParticipantScore,
  getCognitiveParticipants,
  updateParticipantPromptPiece,
  ParticipantSession,
  CognitiveParticipant
} from '@services/airtable';
import { generatePromptPiece } from '@services/groq';
import GatewayGame from './GatewayGame';

export default function SessionDataPage() {
  const [sessions, setSessions] = useState<ParticipantSession[]>([]);
  const [showGame, setShowGame] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [savingIdx, setSavingIdx] = useState<number | null>(null);

  // Participants modal
  const [showParticipants, setShowParticipants] = useState(false);
  const [participants, setParticipants] = useState<CognitiveParticipant[]>([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [generatingIdx, setGeneratingIdx] = useState<number | null>(null);

  const fetchSessions = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getParticipantSessions();
      setSessions(data);
    } catch (err) {
      console.error(err);
      setError('Error al cargar los datos. Revisa la consola.');
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async () => {
    setLoadingParticipants(true);
    try {
      const data = await getCognitiveParticipants();
      setParticipants(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingParticipants(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleCopy = async (text: string, idx: number) => {
    await navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const handleScoreChange = (idx: number, value: string) => {
    setSessions((prev) => prev.map((s, i) => (i === idx ? { ...s, aiScore: value === '' ? 0 : Number(value) } : s)));
  };

  const handleScoreSave = async (idx: number) => {
    const session = sessions[idx];
    if (!session.id) return;
    setSavingIdx(idx);
    try {
      await updateParticipantScore(session.id, session.aiScore ?? 0);
    } catch (err) {
      console.error(err);
      setError('Error al guardar el score.');
    } finally {
      setSavingIdx(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 4000);
      return;
    }
    setDeleting(true);
    setConfirmDelete(false);
    try {
      await deleteAllParticipantSessions();
      setSessions([]);
    } catch (err) {
      console.error(err);
      setError('Error al limpiar la tabla.');
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenParticipants = async () => {
    setShowParticipants(true);
    await fetchParticipants();
  };

  const handleGenerateCard = async (idx: number) => {
    const participant = participants[idx];
    setGeneratingIdx(idx);
    try {
      const existingPieces = participants
        .filter((p) => p.promptPiece && p.id !== participant.id)
        .map((p) => p.promptPiece!);

      const piece = await generatePromptPiece({
        cardType: participant.resultCard,
        participantName: participant.name,
        role: participant.role,
        existingPieces
      });

      // Save to Airtable
      await updateParticipantPromptPiece(participant.id, piece);

      // Update local state
      setParticipants((prev) => prev.map((p, i) => (i === idx ? { ...p, promptPiece: piece } : p)));
    } catch (err) {
      console.error(err);
    } finally {
      setGeneratingIdx(null);
    }
  };

  const getCardColor = (card: string) => {
    const lower = card.toLowerCase();
    if (lower.includes('amarilla')) return '#ffdf00';
    if (lower.includes('magenta')) return '#ea00d9';
    if (lower.includes('cian')) return '#00d2ff';
    if (lower.includes('naranja')) return '#ff6b35';
    return '#2ecc71';
  };

  return (
    <div className="min-h-screen py-12 px-4 dark:bg-black-100">
      {/* Game Mode */}
      {showGame ? (
        <div className="max-w-5xl mx-auto">
          <GatewayGame onExit={() => setShowGame(false)} />
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold text-white">Session Data</h2>
            <p className="text-gray-400 mt-3 text-sm md:text-base">
              Consulta los prompts y asigna puntuaciones a los equipos.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap justify-end gap-3 mb-4">
            <button
              onClick={handleOpenParticipants}
              className="px-4 py-2 text-sm rounded-xl bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/20 hover:text-indigo-200 transition-all"
            >
              👥 Ver Participantes
            </button>
            <button
              onClick={handleDeleteAll}
              disabled={deleting || sessions.length === 0}
              className={`px-4 py-2 text-sm rounded-xl border transition-all ${
                confirmDelete
                  ? 'bg-red-500/20 border-red-500/40 text-red-300'
                  : 'bg-white/5 border-white/10 text-gray-300 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-300'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {deleting ? '⏳ Limpiando...' : confirmDelete ? '⚠️ Click para confirmar' : '🗑️ Limpiar tabla'}
            </button>
            <button
              onClick={fetchSessions}
              disabled={loading}
              className="px-4 py-2 text-sm rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-all"
            >
              {loading ? '⏳ Cargando...' : '🔄 Refrescar'}
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="p-4 mb-6 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* Empty */}
          {!loading && sessions.length === 0 && !error && (
            <div className="text-center text-gray-500 py-20">No hay sesiones registradas aún.</div>
          )}

          {/* Sessions */}
          {sessions.length > 0 && (
            <div className="space-y-4">
              {sessions.map((session, idx) => (
                <motion.div
                  key={session.id || idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="p-5 rounded-2xl bg-white/[0.03] border border-white/10 space-y-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-white">{session.teamName}</h3>
                    <div className="flex items-center gap-2">
                      <label htmlFor={`score-${idx}`} className="text-xs text-gray-500">
                        AI Score:
                      </label>
                      <input
                        id={`score-${idx}`}
                        type="number"
                        value={session.aiScore ?? 0}
                        onChange={(e) => handleScoreChange(idx, e.target.value)}
                        className="w-20 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-indigo-300 text-sm font-bold text-center focus:border-indigo-500 focus:outline-none transition-colors"
                      />
                      <button
                        onClick={() => handleScoreSave(idx)}
                        disabled={savingIdx === idx}
                        className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                          savingIdx === idx
                            ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300 cursor-wait'
                            : 'bg-white/5 border-white/10 text-gray-300 hover:bg-indigo-500/10 hover:border-indigo-500/30 hover:text-indigo-300'
                        }`}
                      >
                        {savingIdx === idx ? '⏳' : '💾'}
                      </button>
                    </div>
                  </div>

                  <div className="relative">
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap bg-white/[0.02] p-4 rounded-xl border border-white/5 max-h-[200px] overflow-y-auto">
                      {session.prompt}
                    </pre>
                    <button
                      onClick={() => handleCopy(session.prompt, idx)}
                      className={`absolute top-2 right-2 px-2 py-1 text-xs rounded-lg border transition-all ${
                        copiedIdx === idx
                          ? 'bg-green-500/20 border-green-500/40 text-green-300'
                          : 'bg-black/50 border-white/10 text-gray-400 hover:text-white hover:bg-black/70'
                      }`}
                    >
                      {copiedIdx === idx ? '✓ Copiado' : '📄 Copiar'}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Participants Modal */}
      <AnimatePresence>
        {showParticipants && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && setShowParticipants(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-3xl max-h-[80vh] overflow-y-auto rounded-2xl bg-gray-900 border border-white/10 p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">👥 Participantes</h3>
                <button
                  onClick={() => setShowParticipants(false)}
                  className="text-gray-400 hover:text-white transition-colors text-xl"
                >
                  ✕
                </button>
              </div>

              {loadingParticipants && (
                <div className="text-center text-gray-500 py-10">⏳ Cargando participantes...</div>
              )}

              {!loadingParticipants && participants.length === 0 && (
                <div className="text-center text-gray-500 py-10">No hay participantes registrados.</div>
              )}

              <div className="space-y-4">
                {participants.map((p, idx) => (
                  <div key={p.id} className="p-4 rounded-xl border border-white/10 bg-white/[0.02] space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="text-white font-semibold">{p.name}</h4>
                        <p className="text-xs text-gray-500">{p.role}</p>
                      </div>
                      <span
                        className="px-3 py-1 rounded-lg text-xs font-bold shrink-0"
                        style={{
                          backgroundColor: `${getCardColor(p.resultCard)}20`,
                          border: `1px solid ${getCardColor(p.resultCard)}60`,
                          color: getCardColor(p.resultCard)
                        }}
                      >
                        {p.resultCard}
                      </span>
                    </div>

                    {/* Prompt Piece */}
                    {p.promptPiece && (
                      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                        <p className="text-xs text-gray-500 mb-1">Prompt Piece:</p>
                        <p className="text-sm text-indigo-300 font-medium">{p.promptPiece}</p>
                      </div>
                    )}

                    {/* Generate Button */}
                    <button
                      onClick={() => handleGenerateCard(idx)}
                      disabled={generatingIdx === idx}
                      className={`w-full px-4 py-2.5 text-sm rounded-xl border transition-all ${
                        generatingIdx === idx
                          ? 'bg-purple-500/20 border-purple-500/40 text-purple-300 cursor-wait'
                          : 'bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border-indigo-500/30 text-indigo-300 hover:from-indigo-600/30 hover:to-purple-600/30 hover:text-white'
                      }`}
                    >
                      {generatingIdx === idx
                        ? '⏳ Generando...'
                        : p.promptPiece
                          ? '🔄 Regenerar Pieza'
                          : '✨ Generar Card'}
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
